/**
 * Thin promise adapters over the callback-based IndexedDB API. Nothing here
 * is consumer-facing; `store.ts` builds the typed surface on top.
 */

import { warn } from './error-codes'
import { OfflineStoreError } from './types'

/** Resolve the IndexedDB factory, or null when the environment lacks one. */
export function getIndexedDB(): IDBFactory | null {
	const g = globalThis as { indexedDB?: IDBFactory }
	if (!g.indexedDB) {
		warn({
			fn: 'getIndexedDB',
			code: 'WEB_OFFLINE_STORE_IDB_UNAVAILABLE',
			message: 'globalThis.indexedDB is unavailable (SSR / disabled / private mode)',
		})
		return null
	}
	return g.indexedDB
}

/** Promisify an `IDBRequest`, mapping its error into the rejection. */
export function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		req.onsuccess = () => resolve(req.result)
		req.onerror = () =>
			reject(
				new OfflineStoreError('tx-aborted', `IndexedDB request failed: ${req.error?.message ?? 'unknown'}`, {
					cause: req.error,
				}),
			)
	})
}

/**
 * Promisify transaction completion. Resolves on `complete`, rejects on
 * `abort`/`error`. Used so callers can `await tx` for durability.
 */
export function txDone(tx: IDBTransaction): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		tx.oncomplete = () => resolve()
		tx.onabort = () =>
			reject(
				new OfflineStoreError('tx-aborted', `transaction aborted: ${tx.error?.message ?? 'unknown'}`, {
					cause: tx.error,
				}),
			)
		tx.onerror = () =>
			reject(
				new OfflineStoreError('tx-aborted', `transaction errored: ${tx.error?.message ?? 'unknown'}`, {
					cause: tx.error,
				}),
			)
	})
}

/** Build an `IDBKeyRange` from the structural {@link import('./types').IndexQuery}. */
export function toKeyRange(q: {
	equals?: IDBValidKey
	lower?: IDBValidKey
	upper?: IDBValidKey
	lowerOpen?: boolean
	upperOpen?: boolean
}): IDBKeyRange | undefined {
	if (q.equals !== undefined) return IDBKeyRange.only(q.equals)
	if (q.lower !== undefined && q.upper !== undefined) {
		return IDBKeyRange.bound(q.lower, q.upper, q.lowerOpen ?? false, q.upperOpen ?? false)
	}
	if (q.lower !== undefined) return IDBKeyRange.lowerBound(q.lower, q.lowerOpen ?? false)
	if (q.upper !== undefined) return IDBKeyRange.upperBound(q.upper, q.upperOpen ?? false)
	return undefined
}
